"use client";

import { Suspense, useEffect, useMemo, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faCheck, faCreditCard, faQrcode } from "@fortawesome/free-solid-svg-icons";
import clsx from "clsx";
import CrownIcon from "@/components/ui/icons/CrownIcon";
import { UnifiedCheckoutModal } from "@/components/payments/UnifiedCheckoutModal";
import { showToast } from "@/components/ui/Toast";
import { useUser } from "@/context/UserContext";
import { PLAN_FEATURES, isPremiumPlan } from "@/constants/plans";

type Plan = {
    id: string;
    label: string;
    price: string;
    cadence: string;
    highlight: boolean;
    heroTag: string;
    premiumDownloads: string;
    freeDownloads: string;
    originalPrice?: string;
    discount?: string;
    isFree?: boolean;
    paymentMethod?: "pix" | "card";
    couponAllowed?: boolean;
    recurrence?: boolean;
    amount: number;
};

type BillingPeriod = "monthly" | "annual";

/** Planos mensais: PIX (esquerda) e Cartão (direita) */
const MONTHLY_PLAN_CODES = ["monthly_pix", "professional"];
/** Planos anuais: PIX (esquerda) e Cartão (direita) */
const ANNUAL_PLAN_CODES = ["annual_pix", "annual_card"];

function mapApiPlanToUI(plan: any): Plan {
    const code = plan.code || String(plan.id);
    const planMeta = PLAN_FEATURES[code as keyof typeof PLAN_FEATURES];

    const durationDays = plan.duration_days ?? 0;
    let cadence = "/sempre";
    if (durationDays === 365) cadence = "/ano";
    else if (durationDays === 30) cadence = "/mês";
    else if (durationDays === 90) cadence = "/trimestre";

    const heroTagMap: Record<string, string> = {
        monthly_pix: "Via PIX",
        professional: "Mais popular",
        annual_pix: "Via PIX",
        annual_card: "Melhor custo-benefício",
    };

    return {
        id: code,
        label: plan.name || planMeta?.name || "Plano",
        price: `R$ ${Number(plan.amount).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`,
        cadence,
        highlight: code === "professional" || code === "annual_card",
        heroTag: heroTagMap[code] || "Premium",
        premiumDownloads: plan.download_limit_premium === 999999 ? "Ilimitados" : String(plan.download_limit_premium ?? 0),
        freeDownloads: plan.download_limit_free === 999999 ? "Ilimitados" : String(plan.download_limit_free ?? 0),
        originalPrice: plan.originalPrice,
        discount: plan.discount,
        isFree: Number(plan.amount) === 0,
        paymentMethod: plan.payment_method ?? planMeta?.paymentMethod,
        couponAllowed: plan.coupon_allowed ?? planMeta?.couponAllowed ?? false,
        recurrence: plan.recurrence ?? planMeta?.recurrence ?? false,
        amount: Number(plan.amount) || 0,
    };
}

const premiumFeatures = [
  "Qualquer arquivo do site",
  "Downloads ilimitados",
  "Velocidade máxima",
  "Atualizações diárias",
  "Liberação imediata"
];

// --- COMPONENTE PRINCIPAL ---

function PlansPageContent() {
    const [allPlans, setAllPlans] = useState<Plan[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>("monthly");
    const searchParams = useSearchParams();
    const router = useRouter();
    const { user, refresh: refreshUser } = useUser();

    const currentPlanCode = user?.currentPlanCode || "free";
    const isPremiumUser = isPremiumPlan(currentPlanCode);

    // Fetch plans from API
    useEffect(() => {
        setLoading(true);
        setError(null);
        fetch("/api/public/plans", { cache: "no-store" })
            .then(async (res) => {
                if (!res.ok) throw new Error("Erro ao buscar planos");
                const data = await res.json();
                if (!data?.plans) throw new Error("Resposta inválida da API");
                setAllPlans(data.plans.map(mapApiPlanToUI));
            })
            .catch((err) => {
                setError(err.message || "Erro ao buscar planos");
            })
            .finally(() => setLoading(false));
    }, []);

    // Filtra os 2 planos visíveis conforme o período selecionado
    const visiblePlans = useMemo(() => {
        const codes = billingPeriod === "monthly" ? MONTHLY_PLAN_CODES : ANNUAL_PLAN_CODES;
        return codes.map(code => allPlans.find(p => p.id === code)).filter(Boolean) as Plan[];
    }, [allPlans, billingPeriod]);

    // Calcula economia anual
    const annualSavings = useMemo(() => {
        const monthlyCard = allPlans.find(p => p.id === "professional");
        const annualCard = allPlans.find(p => p.id === "annual_card");
        if (monthlyCard && annualCard) {
            const yearlyIfMonthly = monthlyCard.amount * 12;
            const saved = yearlyIfMonthly - annualCard.amount;
            return saved > 0 ? saved : 0;
        }
        return 0;
    }, [allPlans]);

    const modalTarget = useMemo(() => {
        if (!selectedPlan) return null;
        return {
            kind: "plan" as const,
            planCode: selectedPlan.id,
            planName: selectedPlan.label,
            amount: selectedPlan.amount,
        };
    }, [selectedPlan]);

    useEffect(() => {
        const checkoutStatus = searchParams.get("checkout");
        const purchaseStatus = searchParams.get("purchase");

        if (checkoutStatus === "success" || purchaseStatus === "success") {
            showToast("Pagamento confirmado! Atualizando suas informações...", "success");
            void refreshUser();
            const params = new URLSearchParams(searchParams.toString());
            params.delete("checkout");
            params.delete("purchase");
            router.replace(`/planos${params.size ? `?${params}` : ""}`);
        }

        if (checkoutStatus === "cancel" || purchaseStatus === "cancel") {
            showToast("Pagamento não concluído.", "error");
            const params = new URLSearchParams(searchParams.toString());
            params.delete("checkout");
            params.delete("purchase");
            router.replace(`/planos${params.size ? `?${params}` : ""}`);
        }
    }, [router, refreshUser, searchParams]);

    const heroTitle = selectedPlan
        ? `Finalize sua assinatura ${selectedPlan.label}`
        : "Assine e desbloqueie todo o acervo";

    const openModal = (plan: Plan) => {
        if (!user) {
            showToast("Faça login para assinar um plano", "info");
            router.push("/login");
            return;
        }
        setSelectedPlan(plan);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedPlan(null);
    };

    const handlePaymentSuccess = async () => {
        await refreshUser();
        closeModal();
    };

    if (loading) {
        return (
            <div className="relative overflow-hidden bg-[#0f011b] text-white">
                <div className="mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 pb-16 pt-28">
                    <div className="text-center">
                        <div className="inline-flex h-12 w-12 animate-spin rounded-full border-4 border-brand-secondary/30 border-t-brand-secondary"></div>
                        <p className="mt-4 text-white/60">Carregando planos...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="relative overflow-hidden bg-[#0f011b] text-white">
                <div className="mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 pb-16 pt-28">
                    <div className="text-center">
                        <p className="text-lg text-red-400">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="relative overflow-hidden bg-[#0f011b] text-white">
            <div className="absolute inset-x-0 top-0 -z-10 h-[420px] bg-[radial-gradient(circle_at_top,_rgba(109,40,217,0.35),_transparent_60%)]" />
            <section className="mx-auto flex min-h-screen max-w-5xl flex-col gap-12 px-4 pb-16 pt-28 sm:px-6 lg:px-8 lg:pb-24">
                <header className="text-center">
                    <div className="inline-flex flex-col items-center">
                        <CrownIcon className="mb-4 h-14 w-14 text-brand-secondary" />
                        <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-1 text-xs uppercase tracking-[0.3em] text-white/60">
                            Planos criativos
                        </span>
                    </div>
                    <h1 className="mt-6 text-4xl font-semibold leading-tight text-white sm:text-5xl">
                        {heroTitle}
                    </h1>
                    <p className="mt-4 text-base text-white/70 sm:text-lg">
                        Escolha o plano que combina com o volume de downloads da sua equipe e tenha acesso imediato a coleções premium, vídeos institucionais e ferramentas avançadas.
                    </p>

                    {/* Switch Mensal / Anual */}
                    <div className="mt-10 flex justify-center">
                        <div className="relative inline-flex items-center rounded-full border border-white/20 bg-white/5 p-1">
                            <button
                                type="button"
                                onClick={() => setBillingPeriod("monthly")}
                                className={clsx(
                                    "relative z-10 rounded-full px-6 py-2.5 text-sm font-semibold transition-colors duration-200",
                                    billingPeriod === "monthly"
                                        ? "bg-brand-secondary text-white shadow-lg shadow-brand-secondary/30"
                                        : "text-white/60 hover:text-white"
                                )}
                            >
                                Mensal
                            </button>
                            <button
                                type="button"
                                onClick={() => setBillingPeriod("annual")}
                                className={clsx(
                                    "relative z-10 rounded-full px-6 py-2.5 text-sm font-semibold transition-colors duration-200",
                                    billingPeriod === "annual"
                                        ? "bg-brand-secondary text-white shadow-lg shadow-brand-secondary/30"
                                        : "text-white/60 hover:text-white"
                                )}
                            >
                                Anual
                            </button>
                            {annualSavings > 0 && billingPeriod === "monthly" && (
                                <span className="ml-2 rounded-full bg-emerald-500/20 px-2.5 py-1 text-[11px] font-semibold text-emerald-300 border border-emerald-500/30">
                                    Economize R$ {annualSavings.toLocaleString("pt-BR", { minimumFractionDigits: 0 })} no anual
                                </span>
                            )}
                        </div>
                    </div>
                </header>

                {/* Cards dos 2 planos visíveis */}
                <div className="flex flex-col gap-6 sm:flex-row sm:justify-center sm:gap-8 sm:items-stretch">
                    {visiblePlans.map((plan) => {
                        const isCurrentPlan = plan.id === currentPlanCode;

                        let buttonText = "Assinar agora";
                        let buttonDisabled = false;
                        let paymentIcon = faArrowRight;

                        if (isCurrentPlan) {
                            buttonText = "Plano Atual";
                            buttonDisabled = true;
                        } else if (plan.paymentMethod === "pix") {
                            buttonText = "Assinar com PIX";
                            paymentIcon = faQrcode;
                        } else if (plan.paymentMethod === "card") {
                            buttonText = "Assinar com Cartão";
                            paymentIcon = faCreditCard;
                        }

                        return (
                            <article
                                key={plan.id}
                                className={clsx(
                                    "relative flex flex-col gap-6 rounded-3xl border border-white/10 bg-white/5 p-8 shadow-soft transition-all duration-300 sm:w-96",
                                    plan.highlight
                                        ? "border-brand-secondary/70 bg-brand-secondary/15 text-white shadow-[0_0_35px_rgba(16,185,129,0.45)] sm:scale-[1.03]"
                                        : "hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(6,182,212,0.25)]",
                                    isCurrentPlan && "ring-2 ring-brand-secondary/50"
                                )}
                            >
                                {/* Badge de plano atual */}
                                {isCurrentPlan && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                        <span className="inline-flex items-center gap-1 rounded-full bg-brand-secondary px-3 py-1 text-xs font-bold text-white shadow-lg">
                                            <FontAwesomeIcon icon={faCheck} className="h-3 w-3" />
                                            Seu plano
                                        </span>
                                    </div>
                                )}

                                <div className="flex flex-col gap-5">
                                    <div className="space-y-3">
                                            <div className="space-y-2 text-left">
                                            <p className="text-xs uppercase tracking-[0.35em] text-white/40">Plano</p>
                                            <h2 className="text-3xl font-semibold">
                                                <span className="bg-clip-text text-transparent bg-[linear-gradient(90deg,#f8fafc,#a855f7_40%,#22d3ee)]">
                                                    {plan.label}
                                                </span>
                                            </h2>
                                            <div className="flex flex-col gap-1 text-white">
                                                <div className="flex items-baseline gap-2">
                                                    <span className="text-4xl font-bold tracking-tight">{plan.price}</span>
                                                    <span className="text-sm text-white/60">{plan.cadence}</span>
                                                </div>
                                            </div>

                                            {/* Badge de renovação/recorrência */}
                                            <span
                                                className={clsx(
                                                    "mt-1 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium w-fit",
                                                    plan.recurrence
                                                        ? "border border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                                                        : "border border-amber-500/30 bg-amber-500/10 text-amber-300"
                                                )}
                                            >
                                                {plan.recurrence
                                                    ? `✅ Recorrência ${billingPeriod === "annual" ? "anual" : "mensal"} automática`
                                                    : "🔄 Renovação manual"}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="rounded-2xl border border-white/10 bg-black/30 px-4 py-4 text-sm text-white/70">
                                        <div className="flex items-center justify-between text-white">
                                            <span className="text-xs uppercase tracking-[0.3em] text-white/40">Downloads Premium</span>
                                            <span className="whitespace-nowrap text-sm font-semibold">{plan.premiumDownloads}</span>
                                        </div>
                                        <hr className="my-3 border-white/10" />
                                        <div className="flex items-center justify-between text-white">
                                            <span className="text-xs uppercase tracking-[0.3em] text-white/40">Downloads Free</span>
                                            <span className="whitespace-nowrap text-sm font-semibold">{plan.freeDownloads}</span>
                                        </div>
                                    </div>
                                </div>

                                <ul className="space-y-3 text-sm text-white/80">
                                    {premiumFeatures.map((feature) => (
                                        <li key={`${plan.id}-${feature}`} className="flex items-start gap-3">
                                            <span
                                                className={clsx(
                                                    "mt-0.5 flex h-6 w-6 items-center justify-center rounded-full border border-white/15",
                                                    plan.highlight ? "bg-white text-brand-secondary" : "bg-brand-secondary/20 text-brand-secondary"
                                                )}
                                            >
                                                <FontAwesomeIcon icon={faCheck} className="h-3 w-3" />
                                            </span>
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>

                                <div className="mt-auto pt-4">
                                    <button
                                        type="button"
                                        onClick={() => !buttonDisabled && openModal(plan)}
                                        disabled={buttonDisabled}
                                        className={clsx(
                                            "group inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold transition",
                                            buttonDisabled
                                                ? "cursor-not-allowed bg-white/10 text-white/50"
                                                : plan.highlight
                                                    ? "bg-white text-brand-secondary hover:bg-white/90"
                                                    : "border border-white/20 bg-white/10 text-white hover:bg-white/20"
                                        )}
                                    >
                                        {!buttonDisabled && (
                                            <FontAwesomeIcon icon={paymentIcon} className="h-4 w-4" />
                                        )}
                                        {buttonText}
                                    </button>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </section>

            <UnifiedCheckoutModal
                isOpen={isModalOpen && Boolean(modalTarget)}
                target={modalTarget}
                onClose={closeModal}
                onSuccess={handlePaymentSuccess}
                pixOnly={selectedPlan?.paymentMethod === "pix"}
                cardOnly={selectedPlan?.paymentMethod === "card"}
            />
        </div>
    );
}

export default function PlansPage() {
    return (
        <Suspense fallback={
            <div className="relative overflow-hidden bg-[#0f011b] text-white">
                <div className="mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 pb-16 pt-28">
                    <div className="text-center">
                        <div className="inline-flex h-12 w-12 animate-spin rounded-full border-4 border-brand-secondary/30 border-t-brand-secondary"></div>
                        <p className="mt-4 text-white/60">Carregando planos...</p>
                    </div>
                </div>
            </div>
        }>
            <PlansPageContent />
        </Suspense>
    );
}
